import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, BookOpen } from "lucide-react";
import * as Icons from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { getCourses } from "../lib/api";

// Department-based color accents (kept tasteful, on-brand)
const DEPT_TONE = {
  Languages: { bg: "bg-[#F5EBD9]", text: "text-[#8C5A1D]", ring: "ring-[#D4AF37]/30" },
  History: { bg: "bg-[#E8DCD0]", text: "text-[#7A4126]", ring: "ring-[#7A4126]/25" },
  "Social Sciences": { bg: "bg-[#DCE7DE]", text: "text-[#1F5C3F]", ring: "ring-[#1F5C3F]/25" },
  Education: { bg: "bg-[#DEE5E2]", text: "text-[#0F4C3A]", ring: "ring-[#0F4C3A]/25" },
  Commerce: { bg: "bg-[#E2E5DC]", text: "text-[#506440]", ring: "ring-[#506440]/25" },
  Vocational: { bg: "bg-[#F2DEDE]", text: "text-[#8C3A3A]", ring: "ring-[#8C3A3A]/25" },
  "Co-curricular": { bg: "bg-[#E5DCE7]", text: "text-[#5A3A7A]", ring: "ring-[#5A3A7A]/25" },
};
const DEFAULT_TONE = { bg: "bg-brand-accent", text: "text-brand-primary", ring: "ring-brand-primary/20" };

export default function Courses() {
  const [ba, setBa] = useState([]);
  const [ma, setMa] = useState([]);

  useEffect(() => {
    getCourses("BA").then(setBa).catch(() => {});
    getCourses("MA").then(setMa).catch(() => {});
  }, []);

  const renderGrid = (list) => (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6" data-testid="courses-grid">
      {list.map((c, i) => {
        const Icon = Icons[c.icon] || BookOpen;
        const tone = DEPT_TONE[c.department] || DEFAULT_TONE;
        return (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}
            className="group relative bg-white rounded-3xl border border-brand-primary/10 lift-card overflow-hidden"
            data-testid={`course-${c.subject.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {/* Decorative corner blob */}
            <div
              className={`absolute -top-12 -right-12 w-40 h-40 rounded-full ${tone.bg} opacity-60 transition-all duration-500 group-hover:scale-125 group-hover:opacity-80`}
              aria-hidden="true"
            />

            <div className="relative p-6 lg:p-7 flex flex-col h-full min-h-[280px]">
              {/* Top row: icon + level */}
              <div className="flex items-start justify-between mb-5">
                <div
                  className={`w-14 h-14 rounded-2xl ${tone.bg} ${tone.text} flex items-center justify-center ring-4 ${tone.ring}`}
                >
                  <Icon className="w-6 h-6" strokeWidth={1.6} />
                </div>
                <span className="px-2.5 py-1 bg-brand-primary text-white text-[10px] font-semibold tracking-[0.25em] uppercase rounded-full">
                  {c.level}
                </span>
              </div>

              {/* Title */}
              <h3 className="font-serif text-2xl lg:text-3xl text-brand-primary leading-tight mb-2">
                {c.subject}
              </h3>

              {/* Department label */}
              <div className={`text-[11px] tracking-[0.2em] uppercase font-semibold mb-3 ${tone.text}`}>
                {c.department}
              </div>

              {/* Description */}
              <p className="text-sm text-brand-ink/75 leading-relaxed mb-5 line-clamp-3 flex-1">
                {c.description}
              </p>

              {/* Footer */}
              <div className="flex items-center justify-between text-xs pt-4 border-t border-brand-primary/10">
                <span className="flex items-center gap-1.5 text-brand-muted">
                  <Clock className="w-3.5 h-3.5" /> {c.duration}
                </span>
                <span className="font-medium tracking-wide text-brand-primary group-hover:text-brand-secondary transition-colors">
                  {c.level} Programme
                </span>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <div data-testid="courses-page">
      <PageHeader
        eyebrow="Academic Programs"
        title="Courses that shape minds and careers."
        subtitle="Explore our Bachelor of Arts and Master of Arts programs, crafted to blend classical learning with contemporary skills."
      />

      <section className="py-16 lg:py-24 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="ba" className="w-full">
            <TabsList className="bg-brand-accent rounded-full p-1 mb-10 mx-auto flex w-fit">
              <TabsTrigger
                value="ba"
                data-testid="tab-ba"
                className="rounded-full px-8 py-2.5 data-[state=active]:bg-brand-primary data-[state=active]:text-white font-medium text-sm"
              >
                Bachelor of Arts (B.A.)
              </TabsTrigger>
              <TabsTrigger
                value="ma"
                data-testid="tab-ma"
                className="rounded-full px-8 py-2.5 data-[state=active]:bg-brand-primary data-[state=active]:text-white font-medium text-sm"
              >
                Master of Arts (M.A.)
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ba">
              <div className="mb-8 max-w-2xl">
                <h2 className="font-serif text-2xl lg:text-3xl text-brand-primary mb-2">
                  B.A. — 3-Year Undergraduate Program
                </h2>
                <p className="text-brand-ink/70 text-sm leading-relaxed">
                  Choose from a rich palette of subjects in languages, humanities,
                  social sciences, and vocational skills.
                </p>
              </div>
              {renderGrid(ba)}
            </TabsContent>

            <TabsContent value="ma">
              <div className="mb-8 max-w-2xl">
                <h2 className="font-serif text-2xl lg:text-3xl text-brand-primary mb-2">
                  M.A. — 2-Year Postgraduate Program
                </h2>
                <p className="text-brand-ink/70 text-sm leading-relaxed">
                  Advance your expertise with research-focused master's programs.
                </p>
              </div>
              {renderGrid(ma)}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Eligibility strip */}
      <section className="py-12 bg-brand-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6 lg:gap-10">
          {[
            { title: "B.A. Eligibility", text: "10+2 pass from any recognized board with minimum aggregate marks as per university norms." },
            { title: "M.A. Eligibility", text: "Bachelor's degree in the relevant discipline with minimum aggregate as per DDU Gorakhpur University rules." },
            { title: "Application Process", text: "Apply online via the admission form, submit documents, and appear for counseling." },
          ].map((b) => (
            <div key={b.title} data-testid={`eligibility-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-5 h-5 text-brand-secondary" />
                <h3 className="font-serif text-xl text-brand-secondary">{b.title}</h3>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
